import { For, Show } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Glyph } from '~/components/glyph';
import { formatDistance } from '~/lib/geo';
import { type GameNode, nodeStore, nodeToTarget } from '~/lib/nodes';
import { nodeView } from '~/lib/node-view';
import type { Ice, Target } from '~/types/game';

function iceColor(ice: Ice): string {
  if (ice === 'HI') return 'var(--hostile)';
  if (ice === 'MED') return 'var(--warn)';
  return 'var(--action)';
}

interface MissionListProps {
  /** Open the hack modal for an engageable target (passed from App). */
  onEngage?: (target: Target) => void;
}

/** JOBS board. Lists the target-role nodes (the engageable jobs) from the
 *  shared `nodeStore`, nearest-first by the same haversine distance the MAP
 *  uses. Shops and services are not jobs and do not appear here. */
export function MissionList(props: MissionListProps) {
  const rows = nodeView.targetsByDistance;

  // Tapping a row selects the node (so the MAP title card previews it on the
  // next visit) and, when it is in range, opens the HackModal directly —
  // subject to the same proximity gate as the MAP ENGAGE button.
  const onRowClick = (node: GameNode) => {
    nodeView.select(node.id);
    if (nodeView.isEngageable(node)) props.onEngage?.(nodeToTarget(node));
  };

  return (
    <>
      <div
        style={{
          padding: '8px 12px',
          'border-bottom': '1px solid var(--line-faint)',
          display: 'flex',
          'align-items': 'center',
          gap: '8px',
        }}
      >
        <Glyph name="jobs" size={16} color="var(--data)" />
        <span class="t-mono" style={{ 'font-size': '14px', color: 'var(--fg)' }}>
          JOB BOARD
        </span>
        <span class="t-micro">/ {rows().length} TARGETS</span>
        <span style={{ flex: 1 }} />
        <span class="t-micro">SORT · DIST ↑</span>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }} class="no-scroll">
        <Show
          when={rows().length > 0}
          fallback={
            <div style={{ padding: '24px 12px', 'text-align': 'center' }}>
              <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>
                {nodeStore.status() === 'error'
                  ? 'NODE DATA UNAVAILABLE'
                  : nodeStore.status() === 'ready'
                    ? 'NO TARGETS'
                    : 'LOADING TARGETS…'}
              </span>
            </div>
          }
        >
          <For each={rows()}>
            {(n) => {
              const inRange = () => nodeView.isInRange(n.id);
              return (
                <div
                  class="row"
                  onClick={() => onRowClick(n)}
                  style={{
                    padding: '12px',
                    'align-items': 'stretch',
                    'flex-direction': 'column',
                    gap: '6px',
                    cursor: 'pointer',
                    opacity: inRange() ? 1 : 0.55,
                  }}
                >
                  <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: '22px',
                        height: '22px',
                        border: '1px solid var(--hostile)',
                        color: 'var(--hostile)',
                        display: 'inline-flex',
                        'align-items': 'center',
                        'justify-content': 'center',
                        'font-family': 'var(--mono)',
                        'font-size': '10px',
                        'letter-spacing': '0.04em',
                        'flex-shrink': 0,
                      }}
                    >
                      {n.tier}
                    </span>
                    <div style={{ 'min-width': 0, display: 'flex', 'flex-direction': 'column' }}>
                      <span
                        class="t-mono"
                        style={{ 'font-size': '12px', color: 'var(--fg)', 'white-space': 'nowrap' }}
                      >
                        {n.label}
                      </span>
                      <span class="t-micro" style={{ color: 'var(--fg-dim)' }}>
                        {n.type}
                      </span>
                    </div>
                    <span style={{ flex: 1 }} />
                    <Show
                      when={inRange()}
                      fallback={<Badge tone="warn">OUT OF RANGE</Badge>}
                    >
                      <Badge tone="action">IN RANGE</Badge>
                    </Show>
                  </div>
                  <div style={{ display: 'flex', gap: '14px', 'align-items': 'center' }}>
                    <div class="t-micro">
                      DIST{' '}
                      <span style={{ color: 'var(--data)' }}>
                        {formatDistance(nodeView.distanceOf(n.id))}
                      </span>
                    </div>
                    <div class="t-micro">
                      ICE <span style={{ color: iceColor(n.iceRating) }}>{n.iceRating}</span>
                    </div>
                    <div class="t-micro">
                      REW <span style={{ color: 'var(--action)' }}>{n.reward}</span>
                    </div>
                    <span style={{ flex: 1 }} />
                    <Glyph name="chev" size={12} color="var(--fg-dim)" />
                  </div>
                </div>
              );
            }}
          </For>
        </Show>
      </div>
    </>
  );
}
